import React from 'react';
import type { VimeoBlockDTO } from 'autogen/swagger/all';
import VideoPlayer from 'components/video/VideoPlayer';
import { AspectRatioContainer } from 'components/generic/productImage/ProductImage';
import styled from 'styled-components';
import { SvgSprite } from 'modules/svgSprite';
import PictureDeprecated from 'components/generic/picture/PictureDeprecated';
import QuickView, { useQuickView } from 'components/quickView';
import { breakpoint, sizes } from 'variables';
import useHasInformationCookieBeenAccepted from 'hooks/user/useHasInformationCookieBeenAccepted';
import { CookieCategory } from 'modules/helpers/cookieInformationHelper';
import VideoCookieOverlay from '../../../VideoCookieOverlay/VideoCookieOverlay';
import useRequestContext from 'hooks/user/useRequestContext';
import format from 'helpers/translations';

export type VimeoPlaceholderProps = {
    model: VimeoBlockDTO;
};

const theme = {
    backgroundColor: '#000'
};

const Container = styled.div`
    margin-bottom: 35px;
`;

const ImageContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

const ImageOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const PlayButton = styled.button`
    background: none;
    border: none;
    &:focus {
        outline: 0;
    }
`;

const SvgPlayIcon = styled(SvgSprite)`
    width: 85px;
    height: 85px;
    box-shadow: 0 0 10px 5px rgba(0, 0, 0, 0.125);
    border-radius: 100%;
`;

const PlayerAspectRatioContainer = styled(AspectRatioContainer)`
    height: 100%;
`;

const StyledQuickView = styled(QuickView)`
    width: 100%;

    && {
        ${breakpoint.up(sizes.md)} {
            width: 768px;
        }
    }
`;

export default function VimeoPlaceholder(props: VimeoPlaceholderProps) {
    const { model } = props;
    const [started, setStarted] = React.useState(model.autoPlay);
    const { toggle } = useQuickView('VimeoQuickView');
    const isModal = model.viewPresentation === 'Modal';
    const isFunctionalCookieAccepted = useHasInformationCookieBeenAccepted(CookieCategory.functional);

    function handlePlay() {
        if (isModal) {
            toggle(true);
        }

        setStarted(true);
    }

    const Player = (
        <PlayerAspectRatioContainer ratioHeight={16} ratioWidth={9}>
            <VideoPlayer
                {...(isModal ? {} : { muted: model.autoPlay })}
                video={{
                    platform: 'Vimeo',
                    url: `https://player.vimeo.com/video/${model.uniqueVideoId}`
                }}
                image={model.image}
            />
        </PlayerAspectRatioContainer>
    );

    const WrappedPlayer = isModal ? (
        <StyledQuickView
            userCanClose
            onClose={() => toggle(false)}
            fullscreenOnMobile
            css={{ background: theme.backgroundColor }}
            quickViewId="VimeoQuickView"
            headerRenderer={() => null}
        >
            {Player}
        </StyledQuickView>
    ) : (
        (started || !isFunctionalCookieAccepted) && Player
    );

    return (
        <Container>
            {WrappedPlayer}
            {(!isModal && started) || (!isFunctionalCookieAccepted && !isModal) ? null : (
                <ImageContainer>
                    <PictureDeprecated {...model.image} />
                    <ImageOverlay>
                        <PlayButton type="button" onClick={handlePlay}>
                            <SvgPlayIcon sprite="VideoPlay" />
                        </PlayButton>
                    </ImageOverlay>
                </ImageContainer>
            )}
            {model.caption && <div>{format(model.caption)}</div>}
        </Container>
    );
}
